import type { DriveStep, Side } from 'driver.js';
import type { HomeViewRole } from '@app/constants/home';
import { EDUCATOR_ROLE, STUDENT_ROLE } from '@core/constants/space';
import {
  HOME_TOUR_EDUCATOR_ORDER,
  HOME_TOUR_STEPS,
  HOME_TOUR_STUDENT_ORDER,
  HOME_TOUR_WELCOME,
  type HomeTourStepId,
} from './homeTourContent';
import { resolveHomeSpacesTourElement } from './resolveHomeSpacesTourElement';

export type HomeTourStepDef = {
  id: string;
  element: string;
  title: string;
  description: string;
  side?: Side;
  align?: 'start' | 'center' | 'end';
};

/** Steps that target card internals — may mount after the parent widget. */
const DYNAMIC_TOUR_STEP_IDS = new Set<string>([
  'space-progress',
  'space-engagement',
  'space-cta',
  'recent-updates-reactions',
  'recent-updates-comments',
]);

export const getHomeTourWelcomeCopy = (
  role: HomeViewRole
): { title: string; description: string } => ({
  title: HOME_TOUR_WELCOME.title,
  description:
    role === EDUCATOR_ROLE
      ? HOME_TOUR_WELCOME.educator
      : HOME_TOUR_WELCOME.student,
});

const resolveStepDef = (
  stepId: HomeTourStepId,
  role: HomeViewRole
): HomeTourStepDef => {
  const step = HOME_TOUR_STEPS[stepId];
  const isEducator = role === EDUCATOR_ROLE;

  const title =
    (isEducator ? step.titleEducator : step.titleStudent) ?? step.title ?? '';

  const description =
    (isEducator ? step.descriptionEducator : step.descriptionStudent) ??
    step.description ??
    '';

  return {
    id: stepId,
    element: step.element,
    title,
    description,
    side: step.side,
    align: step.align,
  };
};

export const getHomeTourStepDefs = (role: HomeViewRole): HomeTourStepDef[] => {
  const order =
    role === EDUCATOR_ROLE ? HOME_TOUR_EDUCATOR_ORDER : HOME_TOUR_STUDENT_ORDER;

  return order.map((stepId) => resolveStepDef(stepId, role));
};

/** @deprecated Use getHomeTourStepDefs(role) — kept for tests that need a full selector list. */
export const HOME_TOUR_STEP_DEFS = getHomeTourStepDefs(STUDENT_ROLE);

const isSpacesWidgetLoading = (root: ParentNode): boolean => {
  const widget = root.querySelector('#home-space-widget');
  return Boolean(widget?.querySelector('[aria-busy="true"]'));
};

const isRecentUpdatesWidgetLoading = (root: ParentNode): boolean => {
  const widget = root.querySelector('#home-recent-updates-widget');
  return Boolean(widget?.querySelector('[aria-busy="true"]'));
};

const shouldIncludeTourStep = (
  step: HomeTourStepDef,
  root: ParentNode
): boolean => {
  // Space-card / recent-update action steps mount after the widget shell. Keep
  // them while cards are already present or the widget is still loading so an
  // early Start includes them. Exclude once the widget has settled empty/error —
  // otherwise skipMissingElement drops them at runtime but progress {{total}}
  // still counts them.
  if (DYNAMIC_TOUR_STEP_IDS.has(step.id)) {
    if (root.querySelector(step.element)) {
      return true;
    }
    if (
      step.id === 'recent-updates-reactions' ||
      step.id === 'recent-updates-comments'
    ) {
      return isRecentUpdatesWidgetLoading(root);
    }
    return isSpacesWidgetLoading(root);
  }

  return Boolean(root.querySelector(step.element));
};

/**
 * Builds driver.js steps for the current home view role.
 * Targets missing from the DOM (hidden widgets, no role toggle, empty cards) are skipped.
 * The spaces step highlights the carousel strip without the greeting card.
 * Space-card steps are kept while spaces are loading so an early Start still
 * includes progress/engagement/CTA once cards mount; empty/error settles omit them.
 */
export const buildHomeTourSteps = (
  role: HomeViewRole = STUDENT_ROLE,
  root: ParentNode = document
): DriveStep[] => {
  return getHomeTourStepDefs(role)
    .filter((step) => shouldIncludeTourStep(step, root))
    .map((step) => {
      const element =
        step.id === 'spaces'
          ? () => resolveHomeSpacesTourElement(root) as Element
          : DYNAMIC_TOUR_STEP_IDS.has(step.id)
          ? () => root.querySelector(step.element) as Element
          : step.element;

      return {
        element,
        data: { stepId: step.id, role },
        popover: {
          title: step.title,
          description: step.description,
          side: step.side ?? 'bottom',
          align: step.align ?? 'start',
        },
      };
    });
};
